import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { ScrollArea } from '../ui/scroll-area';
import { API_URL } from '../../config';
import {
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
  TrendingUp,
  Clock,
  Loader2,
} from 'lucide-react';

const FeedbackStats = ({ documentId }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (documentId) {
      fetchStats();
    }
  }, [documentId]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/documents/${documentId}/feedback/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch feedback stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
        <CardContent className="py-8 text-center">
          <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  if (!stats || stats.total_feedback === 0) {
    return (
      <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
        <CardContent className="py-8 text-center">
          <MessageSquare className="w-12 h-12 mx-auto mb-4 text-slate-300 dark:text-slate-700" />
          <p className="text-slate-500 dark:text-slate-400">No feedback yet</p>
          <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">
            Feedback will appear here once readers rate this article
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800" data-testid="feedback-stats">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-[Manrope] flex items-center gap-2">
          <TrendingUp className="w-5 h-5" />
          Reader Feedback
        </CardTitle>
        <CardDescription>
          Based on {stats.total_feedback} response{stats.total_feedback !== 1 ? 's' : ''}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Overall Rating */}
        <div className="flex items-center gap-4">
          <div className={`text-3xl font-bold ${
            stats.helpful_percentage >= 70 
              ? 'text-green-600 dark:text-green-400' 
              : stats.helpful_percentage >= 40 
                ? 'text-amber-600 dark:text-amber-400'
                : 'text-red-600 dark:text-red-400'
          }`}>
            {stats.helpful_percentage}%
          </div>
          <div className="flex-1">
            <div className="text-sm text-slate-600 dark:text-slate-400 mb-1">
              Found this helpful
            </div>
            <Progress 
              value={stats.helpful_percentage} 
              className={`h-2 ${
                stats.helpful_percentage >= 70 
                  ? '[&>div]:bg-green-500' 
                  : stats.helpful_percentage >= 40 
                    ? '[&>div]:bg-amber-500'
                    : '[&>div]:bg-red-500'
              }`}
            />
          </div>
        </div>

        {/* Stats Breakdown */}
        <div className="grid grid-cols-2 gap-4">
          <div className="p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
            <div className="flex items-center gap-2 text-green-700 dark:text-green-400 mb-1">
              <ThumbsUp className="w-4 h-4" />
              <span className="text-sm font-medium">Helpful</span>
            </div>
            <div className="text-2xl font-bold text-green-800 dark:text-green-300">
              {stats.helpful_count}
            </div>
          </div>
          <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
            <div className="flex items-center gap-2 text-red-700 dark:text-red-400 mb-1">
              <ThumbsDown className="w-4 h-4" />
              <span className="text-sm font-medium">Not Helpful</span>
            </div>
            <div className="text-2xl font-bold text-red-800 dark:text-red-300">
              {stats.not_helpful_count}
            </div>
          </div>
        </div>

        {/* Recent Feedback with Notes */}
        {stats.recent_feedback && stats.recent_feedback.length > 0 && (
          <div className="pt-4 border-t border-slate-200 dark:border-slate-800">
            <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              Recent Feedback Notes
            </h4>
            <ScrollArea className="max-h-[200px]">
              <div className="space-y-3">
                {stats.recent_feedback.map((feedback, index) => (
                  <div 
                    key={feedback.id || index}
                    className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant={feedback.is_helpful ? 'default' : 'destructive'} className="text-xs">
                        {feedback.is_helpful ? 'Helpful' : 'Not Helpful'}
                      </Badge>
                      <span className="text-xs text-slate-500 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDate(feedback.created_at)}
                      </span>
                    </div>
                    {feedback.note && (
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        "{feedback.note}"
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FeedbackStats;
